import browser from 'Cloud/browser';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_FREE_B2B_BIZ_EDITORS_USER, IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import type { SendSharingContentAnalytics, SharingContentProps } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingNewWeblinkTab } from 'reactApp/components/SharingWindow/SharingNew/Weblink/SharingNewWeblinkTab';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/SharingNew/Weblink/WeblinkList/SharingNewWeblinkList.types';
import SharingPersonalAccessBlock from 'reactApp/components/SharingWindow/SharingRedisign/blocks/SharingPersonalAccessBlock/SharingPersonalAccessBlock';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import { getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import { getFeatureEditingNoneditableFormats } from 'reactApp/modules/features/features.selectors';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getItemById, getStorageItemById, isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { weblinkAccessControlLoadStart } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import type { RootState } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './SharingContentRedesign.css';

const enum VIEW {
    options = 'options',
    personalAccess = 'personalAccess',
}

export const SharingContentRedesign = memo(
    // eslint-disable-next-line max-lines-per-function
    (props: IPropsWithPopup<SharingContentProps>) => {
        const { itemStorage, id, from, isPhone, withPromoTooltip, gaSuffix, onboardingEnabled, onClose, fromEditor } = props;
        const getItem = useCallback(
            (state) => (itemStorage ? getStorageItemById(state, itemStorage, id) : getItemById(state, id)),
            [id, itemStorage]
        );

        const item = useSelector(getItem) as PublishItem;
        const isPublic = !!config.get('PUBLIC');

        const [view, setView] = useState(VIEW.options);

        const refRoot = useRef<HTMLDivElement | null>(null);

        const sendSharingContentAnalytics = useCallback<SendSharingContentAnalytics>(({ action, label }) => {
            sendPaymentGa({ eventCategory: 'enable-sharing', action, label });
        }, []);

        useEffect(() => {
            sendSharingContentAnalytics({ action: 'show' });
        }, [sendSharingContentAnalytics]);

        const isFolder = item?.isFolder;
        const isSharedOrMouted = useSelector((state: RootState) =>
            isFolder ? isMountedOrSharedFolder(state, item) : ({} as ReturnType<typeof isMountedOrSharedFolder>)
        );
        const storage = useSelector(getCurrentStorage);
        const { isEmbedded, isStock } = getStorage(storage);

        const isOwn = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
        const noEdit = isStock ? false : isPublic && !isOwn;

        const dispatch = useDispatch();
        const isWindowSmall = useIsWindowIntegrationSmall();
        const editingNoneEditableFormatsFeature = useSelector((state: RootState) => {
            if (!item) {
                return;
            }
            return getFeatureEditingNoneditableFormats(state, {
                file: {
                    ext: typeof item === 'object' && 'ext' in item ? item.ext : undefined,
                    size: typeof item === 'object' && 'size' in item ? item.size : undefined,
                },
                storage,
            });
        });
        const convertExt = editingNoneEditableFormatsFeature?.ext || '';

        const isConvertFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, { ...item, ext: convertExt }));
        const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
        const isEditable = !!useSelector((state: RootState) =>
            getEditorsFor(
                state,
                convertExt
                    ? {
                          ...item,
                          ext: convertExt,
                      }
                    : item
            )
        )?.length;

        const isDocumentForEdit =
            (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER) ||
            isFileForCoEditing ||
            (isConvertFileForCoEditing && !(IS_TABLET_BROWSER || browser.isIpadOs()) && isEditable);
        const isReadOnlyItem = !!item?.isReadOnly;
        const parent = useSelector((state: RootState) => getItemById(state, item?.parent || ''));

        const isReadOnlyParent = !!(parent && 'isReadOnly' in parent && parent?.isReadOnly);
        const isReadOnlyProperty = !isReadOnlyItem && isSharedOrMouted.isMounted ? isReadOnlyParent : isReadOnlyItem;
        const isReadOnly = isFolder ? isReadOnlyProperty : isReadOnlyProperty || !isDocumentForEdit;

        const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));

        const isBizUser = useSelector(UserSelectors.isBizUser);

        const itemName = useMemo(() => {
            if (!item?.name) {
                return null;
            }

            if (item.name.length > 102) {
                return `${item.name.slice(0, 50)}...${item.name.substring(item.name.length - 50, item.name.length)}`;
            }

            return item.name;
        }, [item?.name]);

        // tempexp_17779-start

        const onCloseAccess = useCallback(() => {
            if (storage === EStorageType.sharedLinks) {
                onClose();
            } else if (view === VIEW.personalAccess) {
                setView(VIEW.options);
            }
        }, [storage, view, onClose]);
        // tempexp_17779-end

        useEffect(() => {
            const xrayUserType = isBizUser ? 'biz' : 'b2c';

            sendXray(['sharing-content-redesign-mount', xrayUserType]);

            dispatch(weblinkAccessControlLoadStart({ id: item?.id || '', storage: item?.storage }));
        }, []);

        const viewComponent = useCallback(
            (view) => {
                switch (view) {
                    case VIEW.personalAccess:
                        return (
                            <SharingPersonalAccessBlock
                                onCloseAccess={onCloseAccess}
                                from={from}
                                item={item}
                                isPhone={isPhone}
                                isReadOnly={isReadOnly}
                                isEmbedded={isEmbedded}
                                onBackClick={() => setView(VIEW.options)}
                                copiedWeblinkOnMount={false}
                            />
                        );
                    case VIEW.options:
                    default:
                        return (
                            <SharingNewWeblinkTab
                                id={id}
                                item={item}
                                from={from}
                                isSharedOrMouted={isSharedOrMouted}
                                gaSuffix={gaSuffix}
                                noEdit={noEdit}
                                isOwn={!isPublic || isOwnPublic}
                                isPublic={isPublic}
                                isPhone={isPhone}
                                onUnpublish={onCloseAccess}
                                onCloseModal={onClose}
                                sendSharingContentAnalytics={sendSharingContentAnalytics}
                                withPromoTooltip={withPromoTooltip}
                                openPersonalAccess={() => setView(VIEW.personalAccess)}
                                variant={isBizUser ? SHARING_VARIANT.biz : SHARING_VARIANT.redesign}
                                isEmbedded={isEmbedded}
                                onboardingEnabled={onboardingEnabled}
                                foldSettings={isBizUser}
                                fromEditor={fromEditor}
                                formProps={props}
                            />
                        );
                }
            },
            [
                onCloseAccess,
                from,
                item,
                isPhone,
                isReadOnly,
                isEmbedded,
                id,
                isSharedOrMouted,
                gaSuffix,
                noEdit,
                isPublic,
                isOwnPublic,
                onClose,
                sendSharingContentAnalytics,
                withPromoTooltip,
                isBizUser,
                onboardingEnabled,
                fromEditor,
                props,
            ]
        );

        return (
            <div className={classNames(styles.root, { [styles.root_phone]: isPhone })} ref={refRoot}>
                {view !== VIEW.personalAccess && (
                    <div data-qa-id="sharing-header" className={classNames(styles.header)}>
                        Доступ к файлу {itemName && <>&laquo;{itemName}&raquo;</>}
                    </div>
                )}

                <div className={classNames(styles.root, { [styles.root_phone]: isPhone, [styles.root_tutoria]: isWindowSmall })}>
                    {viewComponent(view)}
                </div>
            </div>
        );
    }
);

SharingContentRedesign.displayName = 'SharingContentRedesign';
